import React, { useEffect, useMemo, useRef, useState } from 'react';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import { getService } from '@Client/runner.hooks/useService';
import { ProcessMapsFilterTabs } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { naturalSortComparator } from '@Shared.Angular/flowingly.helper/naturalSort';
import { TagFilter } from '@Shared.Angular/flowingly.components/TagFilter/TagFilter';
import FlowModelSummary from '@Client/runner.shared/FlowModelSummary/FlowModelSummary';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import Search from '@Shared.Angular/flowingly.components/Search/Search';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

interface Props {
  processMaps: IPublishedFlowModel[];
  onFlowModelClick(id: string): void;
  showMyMapsTab: boolean;
  selectedCategory: string;
}

const ProcessMapList = (props: Props) => {
  const { processMaps, onFlowModelClick, showMyMapsTab, selectedCategory } =
    props;

  const APP_CONFIG = getService('APP_CONFIG');
  const { maxNumberOfDaysForRecentlyUpdated, showDashboardsV1 } = APP_CONFIG;
  const permissionsService = getService('permissionsService');
  const flowinglyConstants = getService('flowinglyConstants');
  const $state = getService('$state');

  const canCreateFlowModels = permissionsService.currentUserHasPermission(
    flowinglyConstants.permissions.FLOWMODEL_CREATE
  );

  const maxRecentlyUpdatedDays =
    maxNumberOfDaysForRecentlyUpdated * 24 * 60 * 60 * 1000;

  const naturallySortedProcessMaps = useMemo(
    () =>
      processMaps.toSorted((leftProcessMap, rightProcessMap) =>
        naturalSortComparator(leftProcessMap.name, rightProcessMap.name)
      ),

    [processMaps]
  );

  const searchInputRef = useRef(null);
  const [selectedButton, setSelectedButton] = useState(
    ProcessMapsFilterTabs.ALL_MAPS_ID
  );
  const [filteredProcessMaps, setFilteredProcessMaps] = useState<
    IPublishedFlowModel[]
  >([]);
  const [searchItem, setSearchItem] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  let emptyStateSummaryText = '';
  let emptyStateBodyText = '';
  let emptyStateButtonText = '';
  let emptyStateOnButtonClick: (() => void) | undefined = undefined;

  const isSearchOrFilterActive = searchItem !== '' || selectedTags.length > 0;

  if (selectedButton === ProcessMapsFilterTabs.RECENTLY_UPDATED_MAPS_ID) {
    emptyStateSummaryText = `There are no maps updated in the last ${maxNumberOfDaysForRecentlyUpdated} days`;
    emptyStateBodyText = '';
    emptyStateButtonText = '';
  } else {
    emptyStateSummaryText = canCreateFlowModels
      ? 'You have no Process Maps'
      : 'View Process Maps';
    emptyStateBodyText = canCreateFlowModels
      ? 'We make it easy for you to create, view and share your organizational Process Maps in one place.<br /> You do not currently have any Process Maps published.'
      : 'You do not currently have any Process Maps available to view. <br /> Please contact your administrator to have relevant Process Maps shared with you.';
    emptyStateButtonText = canCreateFlowModels ? 'Publish a Process Map' : '';
    emptyStateOnButtonClick = canCreateFlowModels
      ? () => $state.go(STATE_MAPPINGS.librarydrafts)
      : undefined;
  }

  const setNewSearchItem = (value: string) => {
    setSearchItem(value.toLowerCase());
  };

  const resetSearchText = () => {
    setSearchItem('');
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  const filterRecentlyUpdatedMaps = (maps) => {
    return maps
      .filter(
        (x) =>
          Date.now() - new Date(x.updatedOn).getTime() <= maxRecentlyUpdatedDays
      )
      .sort((a, b) => {
        return a.updatedOn.localeCompare(b.updatedOn);
      })
      .reverse();
  };

  useEffect(() => {
    if (selectedCategory === 'recently-viewed') {
      setFilteredProcessMaps(processMaps);
    } else {
      setFilteredProcessMaps(naturallySortedProcessMaps);
    }
  }, [naturallySortedProcessMaps, processMaps, selectedCategory]);

  useEffect(() => {
    filterAction();
  }, [searchItem, selectedTags, selectedButton, processMaps, selectedCategory]);

  const filterAction = () => {
    const sourceProcessMaps =
      selectedCategory === 'recently-viewed'
        ? processMaps
        : naturallySortedProcessMaps;

    let filteredValueOnSearch = sourceProcessMaps.filter(
      (x) =>
        x.description?.toLowerCase().includes(searchItem) ||
        x.name?.toLowerCase().includes(searchItem) ||
        x.processOwnerName?.toLowerCase().includes(searchItem)
    );

    filteredValueOnSearch = filteredValueOnSearch.filter(
      (item) =>
        selectedTags.length === 0 ||
        item.parsedTags?.some((tag) => selectedTags.includes(tag.id))
    );

    switch (selectedButton) {
      case ProcessMapsFilterTabs.MAPS_IM_IN_ID:
        setFilteredProcessMaps(
          filteredValueOnSearch.filter((x) => x.isUserAParticipant)
        );
        break;
      case ProcessMapsFilterTabs.ALL_MAPS_ID:
        setFilteredProcessMaps(filteredValueOnSearch);
        break;
      case ProcessMapsFilterTabs.MY_MAPS_ID:
        setFilteredProcessMaps(
          filteredValueOnSearch.filter((x) => x.isUserProcessOwner)
        );
        break;
      case ProcessMapsFilterTabs.RECENTLY_UPDATED_MAPS_ID:
        setFilteredProcessMaps(
          filterRecentlyUpdatedMaps(filteredValueOnSearch)
        );
        break;
      default:
        setFilteredProcessMaps(filteredValueOnSearch);
        break;
    }
  };

  const getTagsUsedInMaps = (maps: IPublishedFlowModel[]) => {
    const allTagIds = maps
      .flatMap((map) => map.parsedTags || [])
      .map((tag) => tag.id);

    return Array.from(new Set(allTagIds));
  };

  return (
    <div className="process-mapv2">
      {processMaps.length > 0 && (
        <div className="toolbar">
          <Search
            id="procedure-search"
            value={searchItem}
            onValueChange={setNewSearchItem}
            placeholder="Search by Map name, description, or Process Owner"
            ariaLabel="Search process maps"
            dataTestId="process-map-search"
          />
          {showDashboardsV1 ? (
            <>
              <div
                aria-label="Basic button group"
                className="button-group-container-process-maps mobile-hide"
              >
                <button
                  id={ProcessMapsFilterTabs.ALL_MAPS_ID}
                  className={`button-group-process-maps ${
                    selectedButton === ProcessMapsFilterTabs.ALL_MAPS_ID
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() =>
                    setSelectedButton(ProcessMapsFilterTabs.ALL_MAPS_ID)
                  }
                >
                  All maps
                </button>
                <button
                  id={ProcessMapsFilterTabs.MAPS_IM_IN_ID}
                  className={`button-group-process-maps ${
                    selectedButton === ProcessMapsFilterTabs.MAPS_IM_IN_ID
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() =>
                    setSelectedButton(ProcessMapsFilterTabs.MAPS_IM_IN_ID)
                  }
                >
                  Maps I'm in
                </button>
                <button
                  id={ProcessMapsFilterTabs.RECENTLY_UPDATED_MAPS_ID}
                  className={`button-group-process-maps ${
                    selectedButton ===
                    ProcessMapsFilterTabs.RECENTLY_UPDATED_MAPS_ID
                      ? 'selected'
                      : ''
                  }`}
                  onClick={() =>
                    setSelectedButton(
                      ProcessMapsFilterTabs.RECENTLY_UPDATED_MAPS_ID
                    )
                  }
                >
                  Recently updated
                </button>
                {showMyMapsTab ? (
                  <button
                    id={ProcessMapsFilterTabs.MY_MAPS_ID}
                    className={`button-group-process-maps ${
                      selectedButton === ProcessMapsFilterTabs.MY_MAPS_ID
                        ? 'selected'
                        : ''
                    }`}
                    onClick={() =>
                      setSelectedButton(ProcessMapsFilterTabs.MY_MAPS_ID)
                    }
                  >
                    My maps
                  </button>
                ) : null}
              </div>
            </>
          ) : null}
          <div className="maps-tag-filter-container mobile-hide">
            <TagFilter
              usedTagIds={getTagsUsedInMaps(processMaps)}
              onApply={(selectedTags) => {
                setSelectedTags(selectedTags);
              }}
            />
          </div>
        </div>
      )}

      {filteredProcessMaps.length > 0 ? (
        <div className="flow-block">
          <div className="flow-list">
            <ul>
              {filteredProcessMaps.map((processMap) => (
                <li key={processMap.id}>
                  <FlowModelSummary
                    flowModelSummary={processMap}
                    onFlowModelClick={onFlowModelClick}
                    resetSearchItem={resetSearchText}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <div>
          <EmptyState
            summaryText={
              isSearchOrFilterActive
                ? 'No matching Maps found'
                : emptyStateSummaryText
            }
            bodyText={
              isSearchOrFilterActive
                ? 'Try adjusting your search or filter criteria.'
                : emptyStateBodyText
            }
            buttonText={isSearchOrFilterActive ? '' : emptyStateButtonText}
            onButtonClick={
              isSearchOrFilterActive ? undefined : emptyStateOnButtonClick
            }
          />
        </div>
      )}
    </div>
  );
};

export default ProcessMapList;
